/**
 * Displays issue attachments with thumbnails for images and download links for files.
 * Images open in a lightbox modal instead of a new tab.
 */

import { useState, useCallback } from 'react';
import { useDeleteAttachment } from '@/hooks/useIssues.js';
import type { JiraAttachment } from '@shared/types.js';

interface AttachmentListProps {
  attachments: JiraAttachment[];
  issueKey: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

// ── Image Lightbox Modal ────────────────────────────────────────────

interface LightboxProps {
  images: JiraAttachment[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function Lightbox({ images, currentIndex, onClose, onNavigate }: LightboxProps) {
  const current = images[currentIndex];
  if (!current) return null;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Previous */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex - 1); }}
          className="absolute left-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Image */}
      <img
        src={`/api/attachments/${current.id}`}
        alt={current.filename}
        className="max-h-[85vh] max-w-[90vw] rounded object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Next */}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex + 1); }}
          className="absolute right-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
          style={{ right: '4rem' }}
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Filename + counter */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-black/50 px-3 py-1.5 text-sm text-white">
        {current.filename}
        {images.length > 1 && <span className="ml-2 text-white/70">{currentIndex + 1} / {images.length}</span>}
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────

export function AttachmentList({ attachments, issueKey }: AttachmentListProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const deleteAttachment = useDeleteAttachment(issueKey);

  const images = attachments.filter((a) => isImage(a.mimeType));
  const files = attachments.filter((a) => !isImage(a.mimeType));

  function handleDelete(id: string, filename: string) {
    if (window.confirm(`Delete "${filename}"?`)) {
      deleteAttachment.mutate(id);
    }
  }

  const openLightbox = useCallback((idx: number) => {
    setLightboxIndex(idx);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  if (attachments.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">
        Attachments ({attachments.length})
      </h3>

      {/* Image thumbnails */}
      {images.length > 0 && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {images.map((img, idx) => (
            <div key={img.id} className="group relative overflow-hidden rounded border">
              <button
                type="button"
                onClick={() => openLightbox(idx)}
                className="w-full text-left"
              >
                <img
                  src={`/api/attachments/${img.id}`}
                  alt={img.filename}
                  className="h-24 w-full object-cover transition-opacity group-hover:opacity-80"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-1.5 py-0.5 text-xs text-white">
                  {img.filename}
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(img.id, img.filename)}
                disabled={deleteAttachment.isPending}
                className="absolute right-1 top-1 hidden rounded bg-black/50 p-0.5 text-white hover:bg-red-600 group-hover:block disabled:opacity-50"
                title="Delete"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((file) => (
            <li key={file.id} className="group flex items-center">
              <a
                href={`/api/attachments/${file.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50"
              >
                <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                <span className="truncate text-blue-600">{file.filename}</span>
                <span className="shrink-0 text-xs text-gray-400">{formatSize(file.size)}</span>
              </a>
              <button
                type="button"
                onClick={() => handleDelete(file.id, file.filename)}
                disabled={deleteAttachment.isPending}
                className="hidden shrink-0 px-2 py-1 text-xs text-gray-400 hover:text-red-500 group-hover:block disabled:opacity-50"
                title="Delete"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
}
